import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import {
  Settings as SettingsIcon,
  Kanban,
  ListChecks,
  ShieldCheck,
  Palette,
  DollarSign,
  Plus,
  Trash2,
  Save,
  Upload,
  AlertTriangle,
  Bell,
} from "lucide-react";
import NotificationRulesPanel from "@/components/NotificationRulesPanel";
import FieldLibrary from "@/components/FieldLibrary";
import TagsManagement from "@/components/TagsManagement";
import SampleData from "@/components/SampleData";
import TerminologyOverrides from "@/components/TerminologyOverrides";
import EmailIngestion from "@/components/EmailIngestion";
import { Type as TypeIcon, Tag as TagIcon, Sparkles, Languages, Mail, Key, Coins } from "lucide-react";
import AIKeys from "@/components/AIKeys";
import UsageCaps from "@/components/UsageCaps";

const SUBNAVS = [
  { key: "pipeline", label: "Pipeline & Stages", icon: Kanban },
  { key: "checklists", label: "Stage Checklists", icon: ListChecks },
  { key: "mappings", label: "Gate-Stage Mapping", icon: ShieldCheck },
  { key: "rules", label: "Notification Rules", icon: Bell },
  { key: "fields", label: "Field Library", icon: TypeIcon },
  { key: "tags", label: "Tags", icon: TagIcon },
  { key: "ai-keys", label: "AI & Model Configuration", icon: Key },
  { key: "usage-caps", label: "Usage Caps", icon: Coins },
  { key: "sample", label: "Sample Data", icon: Sparkles },
  { key: "terminology", label: "Terminology", icon: Languages },
  { key: "email-ingestion", label: "Email Ingestion", icon: Mail },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "currency", label: "Workspace Currency", icon: DollarSign },
];

export default function Settings() {
  const [sub, setSub] = useState(() => {
    // Support ?tab=<subnav-key> deep link (used by OAuth callback redirect)
    try {
      const p = new URLSearchParams(window.location.search).get("tab");
      if (p && SUBNAVS.some((s) => s.key === p)) return p;
    } catch { /* ignore */ }
    return "pipeline";
  });
  return (
    <div className="p-8">
      <div className="cw-caption uppercase tracking-widest">Configuration</div>
      <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
        <SettingsIcon size={22} /> Settings
      </h1>
      <p className="text-sm text-[var(--cw-muted)] mt-1">
        Manage pipelines, checklists, gate-stage mappings, branding, and currency.
      </p>

      <div className="mt-6 grid grid-cols-[220px_1fr] gap-6">
        <nav className="space-y-1" data-testid="settings-subnav">
          {SUBNAVS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSub(s.key)}
              className={`w-full text-left text-sm px-3 py-2 rounded-md inline-flex items-center gap-2 transition ${
                sub === s.key ? "font-semibold" : "text-[var(--cw-muted)]"
              }`}
              style={
                sub === s.key
                  ? {
                      background: "rgba(40,42,116,0.08)",
                      color: "var(--cw-primary)",
                    }
                  : {}
              }
              data-testid={`settings-subnav-${s.key}`}
            >
              <s.icon size={14} />
              {s.label}
            </button>
          ))}
        </nav>

        <div>
          {sub === "pipeline" && <PipelineAndStages />}
          {sub === "checklists" && <StageChecklists />}
          {sub === "mappings" && <GateStageMapping />}
          {sub === "rules" && <PipelineNotificationRules />}
          {sub === "fields" && <FieldLibrary />}
          {sub === "tags" && <TagsManagement />}
          {sub === "ai-keys" && <AIKeys />}
          {sub === "usage-caps" && <UsageCaps />}
          {sub === "sample" && <SampleData />}
          {sub === "terminology" && <TerminologyOverrides />}
          {sub === "email-ingestion" && <EmailIngestion />}
          {sub === "branding" && <Branding />}
          {sub === "currency" && <WorkspaceCurrency />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Pipeline & Stages ---------------- */
function PipelineAndStages() {
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(null);
  const [stages, setStages] = useState([]);
  const [newPipe, setNewPipe] = useState("");
  const [newStage, setNewStage] = useState({ stage_name: "", stagnation_threshold_days: 30 });
  const { tenant } = useAuth();
  const { reloadPipelines } = usePipelineCtx();
  const multiEnabled = !!tenant?.multi_pipeline_enabled;

  const load = () =>
    api.get("/pipelines").then(({ data }) => {
      setPipelines(data);
      if (!pipelineId && data[0]) setPipelineId(data[0].pipeline_id);
    });
  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!pipelineId) return;
    api.get(`/pipelines/${pipelineId}/stages`).then(({ data }) => setStages(data));
  }, [pipelineId]);

  const canAddPipeline = multiEnabled || pipelines.length === 0;

  const addPipeline = async () => {
    const name = (newPipe || "").trim();
    if (!name) return;
    try {
      await api.post("/settings/pipelines", { pipeline_name: name });
      toast.success("Pipeline created");
      setNewPipe("");
      load();
      reloadPipelines();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    }
  };

  const deletePipeline = async (p) => {
    if (!window.confirm(`Delete pipeline "${p.pipeline_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/settings/pipelines/${p.pipeline_id}`);
      toast.success("Pipeline deleted.");
      if (pipelineId === p.pipeline_id) setPipelineId(null);
      load();
      reloadPipelines();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete pipeline.");
    }
  };

  const renamePipeline = async (p) => {
    const name = window.prompt("New pipeline name:", p.pipeline_name);
    if (!name) return;
    await api.patch(`/settings/pipelines/${p.pipeline_id}`, { pipeline_name: name });
    toast.success("Renamed");
    load();
  };

  const addStage = async () => {
    if (!newStage.stage_name.trim()) return;
    await api.post(`/settings/pipelines/${pipelineId}/stages`, newStage);
    toast.success("Stage added");
    setNewStage({ stage_name: "", stagnation_threshold_days: 30 });
    const { data } = await api.get(`/pipelines/${pipelineId}/stages`);
    setStages(data);
  };

  const renameStage = async (s) => {
    const name = window.prompt("New stage name:", s.stage_name);
    if (!name) return;
    await api.patch(`/settings/stages/${s.stage_id}`, { stage_name: name });
    const { data } = await api.get(`/pipelines/${pipelineId}/stages`);
    setStages(data);
  };

  const updateThreshold = async (s, val) => {
    const n = parseInt(val || "0", 10);
    if (!n || n < 1) return;
    await api.patch(`/settings/stages/${s.stage_id}`, { stagnation_threshold_days: n });
    const { data } = await api.get(`/pipelines/${pipelineId}/stages`);
    setStages(data);
  };

  const reorder = async (idx, dir) => {
    const next = [...stages];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setStages(next);
    await api.post(`/settings/pipelines/${pipelineId}/stages/reorder`, {
      stage_ids_ordered: next.map((s) => s.stage_id),
    });
  };

  return (
    <div className="space-y-6">
      <div className="cw-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="cw-h3">Pipelines</h3>
          {!multiEnabled && pipelines.length >= 1 && (
            <div className="text-xs text-[var(--cw-muted)]">
              This workspace is configured for a single pipeline. Contact ProdZen to enable multiple.
            </div>
          )}
        </div>
        <div className="mt-3 space-y-2">
          {pipelines.map((p) => (
            <div
              key={p.pipeline_id}
              className="flex items-center gap-3 border rounded-md p-2 cursor-pointer"
              style={{
                borderColor:
                  p.pipeline_id === pipelineId
                    ? "var(--cw-primary)"
                    : "var(--cw-border)",
                background: p.pipeline_id === pipelineId ? "rgba(40,42,116,0.04)" : "white",
              }}
              onClick={() => setPipelineId(p.pipeline_id)}
              data-testid={`pipeline-row-${p.pipeline_name}`}
            >
              <div className="flex-1 text-sm font-medium">{p.pipeline_name}</div>
              <button
                onClick={(e) => { e.stopPropagation(); renamePipeline(p); }}
                className="cw-btn-ghost text-xs"
                data-testid={`rename-pipeline-${p.pipeline_id}`}
              >
                Rename
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deletePipeline(p); }}
                title="Delete pipeline"
                className="text-xs px-2"
                style={{ color: "var(--cw-error)" }}
                data-testid={`delete-pipeline-${p.pipeline_id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        {canAddPipeline && (
          <div className="mt-3">
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <input
                  value={newPipe}
                  onChange={(e) => setNewPipe(e.target.value)}
                  placeholder="New pipeline name…"
                  className="w-full h-9 px-3 rounded-md border text-sm"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid="new-pipeline-input"
                />
                {newPipe.length > 0 && !newPipe.trim() && (
                  <div className="text-xs mt-1" style={{ color: "var(--cw-error)" }}
                       data-testid="pipeline-name-error">
                    Pipeline name is required.
                  </div>
                )}
              </div>
              <button
                onClick={addPipeline}
                disabled={!newPipe.trim()}
                className="cw-btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="add-pipeline-btn"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>
        )}
      </div>

      {pipelineId && (
        <div className="cw-card p-5">
          <h3 className="cw-h3">Stages</h3>
          <div className="mt-3 space-y-2">
            {stages.map((s, idx) => (
              <div
                key={s.stage_id}
                className="flex items-center gap-3 border rounded-md p-2"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid={`stage-row-${s.stage_name}`}
              >
                <div className="text-xs text-[var(--cw-muted)] w-6">#{idx + 1}</div>
                <div className="flex-1 text-sm font-medium">{s.stage_name}</div>
                <div className="text-xs text-[var(--cw-muted)]">Threshold (days):</div>
                <input
                  type="number"
                  defaultValue={s.stagnation_threshold_days || 30}
                  onBlur={(e) => updateThreshold(s, e.target.value)}
                  className="w-16 h-7 px-2 rounded-md border text-xs"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`stage-threshold-${s.stage_id}`}
                />
                <button
                  onClick={() => reorder(idx, -1)}
                  disabled={idx === 0}
                  className="text-xs px-2 disabled:opacity-30"
                  data-testid={`stage-up-${s.stage_id}`}
                >
                  ↑
                </button>
                <button
                  onClick={() => reorder(idx, 1)}
                  disabled={idx === stages.length - 1}
                  className="text-xs px-2 disabled:opacity-30"
                  data-testid={`stage-down-${s.stage_id}`}
                >
                  ↓
                </button>
                <button
                  onClick={() => renameStage(s)}
                  className="cw-btn-ghost text-xs"
                  data-testid={`rename-stage-${s.stage_id}`}
                >
                  Rename
                </button>
                <StageDeleteButton stage={s} onDeleted={async () => {
                  const { data } = await api.get(`/pipelines/${pipelineId}/stages`);
                  setStages(data);
                }} />
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              value={newStage.stage_name}
              onChange={(e) => setNewStage({ ...newStage, stage_name: e.target.value })}
              placeholder="New stage name…"
              className="flex-1 h-9 px-3 rounded-md border text-sm"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="new-stage-name"
            />
            <input
              type="number"
              value={newStage.stagnation_threshold_days}
              onChange={(e) =>
                setNewStage({ ...newStage, stagnation_threshold_days: parseInt(e.target.value || "30", 10) })
              }
              className="w-20 h-9 px-2 rounded-md border text-sm"
              style={{ borderColor: "var(--cw-border)" }}
              title="Stagnation threshold days"
            />
            <button
              onClick={addStage}
              className="cw-btn-primary text-sm inline-flex items-center gap-1"
              data-testid="add-stage-btn"
            >
              <Plus size={14} /> Add Stage
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Stage Checklists ---------------- */
function StageChecklists() {
  const { pipelineId, pipelines } = usePipelineCtx();
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState("");
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ criteria_text: "", item_type: "required", proof_required: false });

  useEffect(() => {
    if (!pipelineId) return;
    api.get(`/pipelines/${pipelineId}/stages`).then(({ data }) => {
      setStages(data);
      setStageId(data[0]?.stage_id || "");
    });
  }, [pipelineId]);

  const loadItems = () => {
    if (!stageId) return;
    api
      .get(`/settings/stages/${stageId}/checklist-templates`)
      .then(({ data }) => setItems(data));
  };
  useEffect(() => { loadItems(); }, [stageId]); // eslint-disable-line

  const add = async () => {
    if (!newItem.criteria_text.trim()) return;
    await api.post(`/settings/stages/${stageId}/checklist-templates`, newItem);
    setNewItem({ criteria_text: "", item_type: "required", proof_required: false });
    loadItems();
    toast.success("Item added");
  };

  const update = async (item, changes) => {
    await api.patch(`/settings/checklist-templates/${item.checklist_item_id}`, changes);
    loadItems();
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.criteria_text}"?`)) return;
    await api.delete(`/settings/checklist-templates/${item.checklist_item_id}`);
    loadItems();
  };

  const currentPipe = pipelines.find((p) => p.pipeline_id === pipelineId);

  return (
    <div className="space-y-4">
      <div className="cw-card p-5 space-y-3">
        <div className="text-xs text-[var(--cw-muted)]">
          Managing checklists for <strong>{currentPipe?.pipeline_name || "…"}</strong>.
          Use the pipeline switcher in the sidebar to change context.
        </div>
        <div>
          <div className="cw-caption">Stage</div>
          <select
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
            className="w-full h-9 px-3 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="checklist-stage-select"
          >
            {stages.map((s) => (
              <option key={s.stage_id} value={s.stage_id}>
                {s.stage_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="cw-card p-5">
        <h3 className="cw-h3">Checklist items</h3>
        <div className="mt-3 space-y-2" data-testid="checklist-items">
          {items.map((it) => (
            <div
              key={it.checklist_item_id}
              className="flex items-center gap-3 border rounded-md p-2"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid={`checklist-item-${it.checklist_item_id}`}
            >
              <input
                defaultValue={it.criteria_text}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== it.criteria_text) update(it, { criteria_text: v });
                }}
                className="flex-1 text-sm px-2 py-1 rounded border"
                style={{ borderColor: "var(--cw-border)" }}
              />
              <label className="text-xs inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={it.item_type === "required"}
                  onChange={(e) =>
                    update(it, { item_type: e.target.checked ? "required" : "optional" })
                  }
                  data-testid={`checklist-req-${it.checklist_item_id}`}
                />
                Required
              </label>
              <label className="text-xs inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={!!it.proof_required}
                  onChange={(e) => update(it, { proof_required: e.target.checked })}
                />
                Proof
              </label>
              <button
                onClick={() => del(it)}
                className="cw-btn-ghost text-xs text-[var(--cw-error)]"
                data-testid={`checklist-del-${it.checklist_item_id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-xs text-[var(--cw-muted)] italic">No items yet.</div>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          <input
            value={newItem.criteria_text}
            onChange={(e) => setNewItem({ ...newItem, criteria_text: e.target.value })}
            placeholder="Item text…"
            className="flex-1 h-9 px-3 rounded-md border text-sm"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="new-checklist-text"
          />
          <label className="text-xs inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={newItem.item_type === "required"}
              onChange={(e) =>
                setNewItem({ ...newItem, item_type: e.target.checked ? "required" : "optional" })
              }
            />
            Required
          </label>
          <label className="text-xs inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={newItem.proof_required}
              onChange={(e) => setNewItem({ ...newItem, proof_required: e.target.checked })}
            />
            Proof
          </label>
          <button
            onClick={add}
            className="cw-btn-primary text-sm inline-flex items-center gap-1"
            data-testid="add-checklist-btn"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Gate-Stage Mapping ---------------- */
function GateStageMapping() {
  const { pipelineId, pipelines } = usePipelineCtx();
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState("");
  const [gates, setGates] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedGate, setSelectedGate] = useState("");

  useEffect(() => {
    if (!pipelineId) return;
    // v5.7 Addition 13: gates are pipeline-scoped
    api.get("/gates", { params: { pipeline_id: pipelineId } })
      .then(({ data }) => setGates(data.filter((g) => g.is_published)));
  }, [pipelineId]);

  useEffect(() => {
    if (!pipelineId) return;
    api.get(`/pipelines/${pipelineId}/stages`).then(({ data }) => {
      setStages(data);
      setStageId(data[0]?.stage_id || "");
    });
  }, [pipelineId]);

  const loadMappings = () =>
    api
      .get("/settings/gate-stage-mappings", { params: { pipeline_id: pipelineId } })
      .then(({ data }) => setMappings(data));

  useEffect(() => {
    if (!pipelineId) return;
    loadMappings();
  }, [pipelineId]); // eslint-disable-line

  const add = async () => {
    if (!selectedGate || !stageId) return;
    try {
      await api.post("/settings/gate-stage-mappings", {
        pipeline_id: pipelineId,
        stage_id: stageId,
        gate_id: selectedGate,
      });
      toast.success("Mapping added");
      setSelectedGate("");
      loadMappings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (m) => {
    await api.patch(`/settings/gate-stage-mappings/${m.mapping_id}`, {
      is_active: !m.is_active,
    });
    loadMappings();
  };

  const del = async (m) => {
    if (!window.confirm(`Remove mapping "${m.gate_name}"?`)) return;
    await api.delete(`/settings/gate-stage-mappings/${m.mapping_id}`);
    loadMappings();
  };

  const stageMappings = mappings.filter((m) => m.stage_id === stageId);
  const hasUnpublishedDeactivated = stageMappings.some(
    (m) => !m.is_active && !m.gate_is_published
  );

  const currentPipe = pipelines.find((p) => p.pipeline_id === pipelineId);

  return (
    <div className="space-y-4">
      <div className="cw-card p-5">
        <div className="text-xs text-[var(--cw-muted)] mb-3">
          Mapping gates for <strong>{currentPipe?.pipeline_name || "…"}</strong>.
          Use the pipeline switcher in the sidebar to change context.
        </div>
        <div>
          <div className="cw-caption">Stage</div>
          <select
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
            className="w-full h-9 px-3 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="mapping-stage-select"
          >
            {stages.map((s) => (
              <option key={s.stage_id} value={s.stage_id}>
                {s.stage_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="cw-card p-5">
        <h3 className="cw-h3">Mapped gates</h3>
        <div className="text-xs text-[var(--cw-muted)] mt-1">
          Mappings trigger an in-app notification when a deal enters this stage. Nothing is auto-run.
        </div>
        {hasUnpublishedDeactivated && (
          <div
            className="mt-3 p-3 rounded-md text-xs flex items-start gap-2"
            style={{
              background: "rgba(243,191,92,0.15)",
              border: "1px solid rgba(243,191,92,0.4)",
              color: "#7A5900",
            }}
            data-testid="unpublished-gate-banner"
          >
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div>
              One or more gates below were unpublished and their mappings were automatically deactivated.
              Re-publish the gate to reactivate, or remove the mapping.
            </div>
          </div>
        )}
        <div className="mt-3 space-y-2" data-testid="mappings-list">
          {stageMappings.map((m) => (
            <div
              key={m.mapping_id}
              className="flex items-center gap-3 border rounded-md p-2"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid={`mapping-row-${m.mapping_id}`}
            >
              <div className="flex-1 text-sm font-medium">{m.gate_name}</div>
              <label className="text-xs inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={!!m.is_active}
                  onChange={() => toggle(m)}
                  data-testid={`mapping-toggle-${m.mapping_id}`}
                />
                Active
              </label>
              <button
                onClick={() => del(m)}
                className="cw-btn-ghost text-xs text-[var(--cw-error)]"
                data-testid={`mapping-del-${m.mapping_id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {stageMappings.length === 0 && (
            <div className="text-xs text-[var(--cw-muted)] italic">No mappings for this stage.</div>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          <select
            value={selectedGate}
            onChange={(e) => setSelectedGate(e.target.value)}
            className="flex-1 h-9 px-3 rounded-md border text-sm"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="mapping-gate-select"
          >
            <option value="">Select a published gate…</option>
            {gates.map((g) => (
              <option key={g.gate_id} value={g.gate_id}>
                {g.gate_name}
              </option>
            ))}
          </select>
          <button
            onClick={add}
            disabled={!selectedGate}
            className="cw-btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="add-mapping-btn"
          >
            <Plus size={14} /> Map
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Branding ---------------- */
function Branding() {
  const { tenant, refresh } = useAuth();
  const [form, setForm] = useState({
    primary_color: "#282A74",
    secondary_color: "#F3BF5C",
    org_name: "",
    fund_name: "",
    tagline: "",
    logo_url: "",
  });

  useEffect(() => {
    if (tenant) {
      setForm({
        primary_color: tenant.primary_color || "#282A74",
        secondary_color: tenant.secondary_color || "#F3BF5C",
        org_name: tenant.org_name || "",
        fund_name: tenant.fund_name || "",
        tagline: tenant.tagline || "",
        logo_url: tenant.logo_url || "",
      });
    }
  }, [tenant?.tenant_id]); // eslint-disable-line

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/(image\/(png|jpe?g|gif))/.test(f.type)) {
      toast.error("Only PNG, JPG, or GIF allowed");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Logo must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, logo_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    // CW6.2.7: client-side trim + empty guard mirrors backend validation
    const trimmedOrg = (form.org_name || "").trim();
    if (!trimmedOrg) {
      toast.error("Org name is required");
      return;
    }
    try {
      await api.patch("/tenant/branding", { ...form, org_name: trimmedOrg });
      toast.success("Branding saved");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm mt-1";

  return (
    <div className="cw-card p-5 space-y-4 max-w-2xl">
      <h3 className="cw-h3">Branding</h3>
      <div>
        <div className="cw-caption">Logo (PNG, JPG, GIF — up to 5MB)</div>
        <div className="flex items-center gap-3 mt-1">
          {form.logo_url && (
            <img src={form.logo_url} alt="logo preview" className="h-12 w-auto rounded"
              data-testid="logo-preview" />
          )}
          <label className="cw-btn-ghost text-sm inline-flex items-center gap-1 cursor-pointer">
            <Upload size={14} />
            Upload logo
            <input type="file" hidden accept="image/png,image/jpeg,image/gif" onChange={onLogo}
              data-testid="logo-input" />
          </label>
          {form.logo_url && (
            <button
              onClick={() => setForm((s) => ({ ...s, logo_url: "" }))}
              className="cw-btn-ghost text-xs text-[var(--cw-error)]"
              data-testid="logo-clear"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="cw-caption">Primary color</div>
          <input
            type="color"
            value={form.primary_color}
            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            className="h-10 w-16 mt-1 rounded"
            data-testid="primary-color"
          />
          <input
            value={form.primary_color}
            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            className="ml-2 h-8 px-2 border rounded text-xs w-24"
            style={{ borderColor: "var(--cw-border)" }}
          />
        </div>
        <div>
          <div className="cw-caption">Secondary color</div>
          <input
            type="color"
            value={form.secondary_color}
            onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
            className="h-10 w-16 mt-1 rounded"
            data-testid="secondary-color"
          />
          <input
            value={form.secondary_color}
            onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
            className="ml-2 h-8 px-2 border rounded text-xs w-24"
            style={{ borderColor: "var(--cw-border)" }}
          />
        </div>
      </div>

      <div>
        <div className="cw-caption">Org name</div>
        <input
          value={form.org_name}
          onChange={(e) => setForm({ ...form, org_name: e.target.value })}
          className={inp}
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="branding-org-name"
        />
      </div>
      <div>
        <div className="cw-caption">Fund name</div>
        <input
          value={form.fund_name}
          onChange={(e) => setForm({ ...form, fund_name: e.target.value })}
          className={inp}
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="branding-fund-name"
        />
      </div>
      <div>
        <div className="cw-caption">Tagline</div>
        <input
          value={form.tagline}
          onChange={(e) => setForm({ ...form, tagline: e.target.value })}
          className={inp}
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="branding-tagline"
        />
      </div>

      <div className="flex justify-end">
        <button onClick={save} className="cw-btn-primary text-sm inline-flex items-center gap-1"
          data-testid="branding-save">
          <Save size={14} /> Save Branding
        </button>
      </div>
    </div>
  );
}

/* ---------------- Workspace Currency ---------------- */
function WorkspaceCurrency() {
  const { tenant, refresh } = useAuth();
  const [ccy, setCcy] = useState("INR");

  useEffect(() => {
    if (tenant?.primary_currency) setCcy(tenant.primary_currency);
  }, [tenant?.primary_currency]);

  const save = async () => {
    try {
      await api.patch("/tenant/currency", { primary_currency: ccy });
      toast.success(`Workspace currency set to ${ccy}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="cw-card p-5 space-y-4 max-w-xl">
      <h3 className="cw-h3">Workspace Currency</h3>
      <p className="text-sm text-[var(--cw-muted)]">
        Every deal's monetary fields are understood to be in this one currency.
        Changing it only changes the label shown next to fields — existing values are not converted.
      </p>
      <div>
        <div className="cw-caption">Currency</div>
        <select
          value={ccy}
          onChange={(e) => setCcy(e.target.value)}
          className="w-full h-9 px-3 rounded-md border text-sm mt-1"
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="currency-select"
        >
          {["INR", "USD", "EUR", "GBP", "AED", "SGD", "JPY", "AUD"].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div className="flex justify-end">
        <button onClick={save} className="cw-btn-primary text-sm inline-flex items-center gap-1"
          data-testid="currency-save">
          <Save size={14} /> Save Currency
        </button>
      </div>
    </div>
  );
}

/* --------------- Stage Delete Button (v5.6 #1) --------------- */
function StageDeleteButton({ stage, onDeleted }) {
  const [info, setInfo] = useState(null);
  const [confirm, setConfirm] = useState(false); // 1st confirm shown
  const [confirm2, setConfirm2] = useState(false); // 2nd confirm (cascade)
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  const check = async () => {
    try {
      const { data } = await api.get(`/settings/stages/${stage.stage_id}/removability`);
      setInfo(data);
      setChecked(true);
      if (!data.removable) {
        // Cw6_2_5 Bug 2: spec says a top-right toast — not a tooltip.
        toast.error(
          "Unable to delete stage. Deals are associated with this stage. Move them to another stage first."
        );
        return;
      }
      if (data.has_dependent_config) {
        setConfirm2(true);
      } else {
        setConfirm(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to check");
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/settings/stages/${stage.stage_id}`);
      toast.success("Stage deleted.");
      setConfirm(false);
      setConfirm2(false);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={check}
        title="Remove stage"
        className="text-xs px-2"
        style={{ color: "var(--cw-error)" }}
        disabled={busy}
        data-testid={`stage-delete-${stage.stage_id}`}
      >
        <Trash2 size={12} />
      </button>

      {confirm && !confirm2 && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--cw-border)" }}>
              <h3 className="cw-h3 text-[var(--cw-error)]">Remove stage</h3>
            </div>
            <div className="p-5 text-sm">
              Remove stage <strong>"{stage.stage_name}"</strong>? This cannot be undone.
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2" style={{ borderColor: "var(--cw-border)" }}>
              <button onClick={() => setConfirm(false)} className="cw-btn-ghost text-xs">Cancel</button>
              <button onClick={remove} disabled={busy}
                className="text-xs rounded-md px-3 py-1.5 border"
                style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
                data-testid="stage-delete-confirm-1">
                {busy ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirm2 && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--cw-border)" }}>
              <h3 className="cw-h3 text-[var(--cw-error)]">Remove stage & related config</h3>
            </div>
            <div className="p-5 text-sm space-y-2">
              <div>
                This stage has{" "}
                {info?.checklist_template_count > 0 && (
                  <><strong>{info.checklist_template_count}</strong> checklist item{info.checklist_template_count === 1 ? "" : "s"}</>
                )}
                {info?.checklist_template_count > 0 && info?.gate_mapping_count > 0 && " and "}
                {info?.gate_mapping_count > 0 && (
                  <><strong>{info.gate_mapping_count}</strong> mapped gate{info.gate_mapping_count === 1 ? "" : "s"}</>
                )}
                . Removing it will also remove these. Continue?
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2" style={{ borderColor: "var(--cw-border)" }}>
              <button onClick={() => setConfirm2(false)} className="cw-btn-ghost text-xs">Cancel</button>
              <button onClick={remove} disabled={busy}
                className="text-xs rounded-md px-3 py-1.5 border"
                style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
                data-testid="stage-delete-confirm-2">
                {busy ? "Removing…" : "Remove & cascade"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* --------------- Pipeline-level Notification Rules (v5.6 #8) --------------- */
function PipelineNotificationRules() {
  const { pipelineId, pipelines } = usePipelineCtx();
  const currentPipe = pipelines.find((p) => p.pipeline_id === pipelineId);

  return (
    <div className="space-y-4">
      <div className="cw-card p-5">
        <div className="text-xs text-[var(--cw-muted)]">
          Managing notification rules for <strong>{currentPipe?.pipeline_name || "…"}</strong>.
          Use the pipeline switcher in the sidebar to change context.
        </div>
      </div>

      {pipelineId && (
        <div className="cw-card p-5">
          <NotificationRulesPanel
            scope="pipeline"
            pipelineId={pipelineId}
            canManage={true}
          />
        </div>
      )}
    </div>
  );
}

